import React, { useState, useCallback, useEffect } from 'react';
import styled from 'styled-components';
import FlexboxContainer from 'components/UI/FlexboxContainer';
import Button from 'components/UI/Button';
import BrandLogoDisplay from 'components/UI/BrandLogoDisplay';
import VendorSelectionDropdown from 'components/UI/VendorSelectionDropdown';
import BusinessUnitSelectionDropdown from 'components/UI/BusinessUnitSelectionDropdown';
import Modal from 'components/UI/Modal';
import { Space } from 'styles/themeSpaces';
import { Text, Border, Background, Main } from 'styles/themeColors';
import { BUSINESS_UNIT_SECTION } from 'constants/credit';
import { useBusinessUnit } from 'context/businessUnit';
import { useVendorBusinessUnitSelection } from 'hooks/useVendorBusinessUnitSelection';
import { useAPI } from 'lib/API';
import { assignBusinessUnitToCompany, removeBusinessUnitFromCompany } from 'requests/company';
import { renderToast, toast } from 'components/UI/ToastNotification';
import Vendor from 'data/Vendor';
import { BusinessUnit } from 'data/BusinessUnit';

const TextSecondary = styled.span`
  color: ${Text.secondary} !important;
  margin-top: ${Space.spBasePlus};
`;

const DropdownsRow = styled(FlexboxContainer)`
  margin-top: ${Space.spBasePlus};
`;

const ButtonContainer = styled.div`
  display: flex;
  align-items: flex-end;
  height: 100%;
  padding-bottom: 0;
`;

interface BusinessUnitCardProps {
  companyId: string;
  vendorsList: Vendor[];
  initialVendor?: Vendor | null;
  initialBusinessUnit?: BusinessUnit | null;
  hasActiveSubscriptions?: boolean;
  onAssignSuccess?: () => void;
  onRemoveSuccess?: () => void;
}

const BusinessUnitCard: React.FC<BusinessUnitCardProps> = ({
  companyId,
  vendorsList,
  initialVendor,
  initialBusinessUnit,
  hasActiveSubscriptions = false,
  onAssignSuccess,
  onRemoveSuccess
}) => {
  const [selectedVendor, setSelectedVendor] = useState<Vendor | null>(initialVendor || null);
  const [selectedBusinessUnit, setSelectedBusinessUnit] = useState<BusinessUnit | null>(initialBusinessUnit || null);
  const [isApplied, setIsApplied] = useState(!!(initialVendor && initialBusinessUnit));
  const [isLoading, setIsLoading] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const { businessUnitList } = useBusinessUnit();

  // Update state when initial values change (e.g., after company data loads)
  useEffect(() => {
    if (initialVendor && initialBusinessUnit) {
      setSelectedVendor(initialVendor);
      setSelectedBusinessUnit(initialBusinessUnit);
      setIsApplied(true);
    }
  }, [initialVendor, initialBusinessUnit]);

  const {
    vendorOptions,
    businessUnitOptions,
    filteredBusinessUnits,
    brandLogoUrl
  } = useVendorBusinessUnitSelection({
    vendorsList,
    businessUnitList,
    selectedVendor,
    selectedBusinessUnit
  });

  // Filter out "All" option for business unit dropdown
  const buOptions = businessUnitOptions.filter(opt => opt.id !== 'all');

  const handleVendorChange = useCallback((vendorName: string) => {
    const vendor = vendorsList.find(v => v.name === vendorName);
    setSelectedVendor(vendor || null);
    setSelectedBusinessUnit(null);
    setIsApplied(false);
  }, [vendorsList]);

  const handleBusinessUnitChange = useCallback((buId: string | null) => {
    if (!buId) {
      setSelectedBusinessUnit(null);
      return;
    }
    const bu = filteredBusinessUnits.find(b => b.id?.toString() === buId);
    setSelectedBusinessUnit(bu || null);
    setIsApplied(false);

    // Auto-select vendor based on business unit's vendorId
    if (bu?.vendorId) {
      const vendor = vendorsList.find(v => v.id === bu.vendorId);
      if (vendor) {
        setSelectedVendor(vendor);
      }
    }
  }, [filteredBusinessUnits, vendorsList]);

  const isFormValid = (selectedVendor && selectedBusinessUnit) || isApplied;

  const assignReq = useAPI({
    deferFn: assignBusinessUnitToCompany,
    onResolve: () => {
      setIsApplied(true);
      setIsLoading(false);
      renderToast(toast.TYPE.SUCCESS, 'Business Unit assigned successfully');
      onAssignSuccess?.();
    },
    onReject: (err) => {
      setIsLoading(false);
      renderToast(toast.TYPE.ERROR, err.message || 'Failed to assign Business Unit');
    }
  });

  const removeReq = useAPI({
    deferFn: removeBusinessUnitFromCompany,
    onResolve: () => {
      setSelectedVendor(null);
      setSelectedBusinessUnit(null);
      setIsApplied(false);
      setIsLoading(false);
      renderToast(toast.TYPE.SUCCESS, 'Business Unit removed successfully');
      onRemoveSuccess?.();
    },
    onReject: (err) => {
      setIsLoading(false);
      renderToast(toast.TYPE.ERROR, err.message || 'Failed to remove Business Unit');
    }
  });

  const onApply = useCallback(() => {
    if (selectedBusinessUnit?.id && companyId) {
      setIsLoading(true);
      assignReq.run(selectedBusinessUnit.id, companyId);
    }
  }, [selectedBusinessUnit, companyId, assignReq]);

  const performRemove = useCallback(() => {
    if (companyId) {
      setIsLoading(true);
      removeReq.run(companyId);
    }
  }, [companyId, removeReq]);

  const onRemove = useCallback(() => {
    if (hasActiveSubscriptions) {
      setShowConfirmModal(true);
    } else {
      performRemove();
    }
  }, [hasActiveSubscriptions, performRemove]);

  const onConfirmRemove = useCallback(() => {
    setShowConfirmModal(false);
    performRemove();
  }, [performRemove]);

  const onCancelRemove = useCallback(() => {
    setShowConfirmModal(false);
  }, []);

  return (
    <>
      <FlexboxContainer
        flexDirection="column"
        padding={Space.sp2x}
        borderColor={`${Border.light} !important`}
        bgColor={Background.light}
        className="border"
        width="50%"
      >
        <h5>{BUSINESS_UNIT_SECTION.TITLE}</h5>
        <TextSecondary className="dense-italic">
          {BUSINESS_UNIT_SECTION.DESCRIPTION}
        </TextSecondary>

        <FlexboxContainer flexDirection="column" margin={`${Space.spBasePlus} 0`}>
          <DropdownsRow justifyContent="flex-start" alignItems="flex-end" gap={Space.sp3x}>
            <BrandLogoDisplay
              logoUrl={brandLogoUrl}
              brandName={selectedBusinessUnit?.brand?.name}
            />
            <VendorSelectionDropdown
              selectedVendorName={selectedVendor?.name || ''}
              vendorOptions={vendorOptions}
              onVendorChange={handleVendorChange}
              disabled={isApplied || isLoading}
            />
            <BusinessUnitSelectionDropdown
              selectedBusinessUnitName={selectedBusinessUnit?.name || ''}
              businessUnitOptions={buOptions}
              onBusinessUnitChange={handleBusinessUnitChange}
              disabled={isApplied || isLoading}
              showAllOption={false}
            />
            <ButtonContainer>
              <Button
                onClick={isApplied ? onRemove : onApply}
                disabled={!isFormValid || isLoading}
                background={Main.gray}
                borderColor={Main.gray}
                height="36px"
              >
                {isApplied
                    ? BUSINESS_UNIT_SECTION.BUTTON_REMOVE
                    : BUSINESS_UNIT_SECTION.BUTTON_APPLY
                }
              </Button>
            </ButtonContainer>
          </DropdownsRow>
        </FlexboxContainer>
      </FlexboxContainer>

      <Modal
        title={BUSINESS_UNIT_SECTION.MODAL_TITLE}
        onClose={onCancelRemove}
        showModal={showConfirmModal}
        theme="accent"
        onClick={onConfirmRemove}
        primaryButtonText={BUSINESS_UNIT_SECTION.MODAL_BUTTON_CONFIRM}
        showGhostButton={true}
        ghostButtonText={BUSINESS_UNIT_SECTION.MODAL_BUTTON_CANCEL}
        onGhostButtonClick={onCancelRemove}
        primaryButtonColor={Main.brandDark}
        color={Main.darkGray}
        borderColor={Main.lightGray}
      >
        <p>{BUSINESS_UNIT_SECTION.MODAL_DESCRIPTION}</p>
      </Modal>
    </>
  );
};

export default BusinessUnitCard;
